import { useTranslateKey } from "../../hooks/useTranslate";
import { useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { Box, Typography, CircularProgress, Divider } from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from "@mui/icons-material/Close";

import { DrawerButton } from "./DrawerButton";

import { Avatar } from "@unicity/design-system";
import { LargeAvatarModal } from "./LargeAvatarModal";
import { CopyToClipboard } from "../CopyToClipboard";
import { MemberErrorScreen } from "../../screens/ErrorScreen/MemberErrorScreen";
import { CustomerTypeChip } from "../CustomerTypeChip"

import { useAuth } from "../../hooks/useAuth";
import { isDateInFuture } from "../../common/date-functions";
import { T } from "../../hooks/useTranslate";

import {
  subscriptionDate,
  officeAvatarWrapper,
} from "./drawer-styles";
import { formatSubscriptionDate } from "../../common/format-functions";

import { MemberContext } from "../../hooks/MemberContext";

import { ProfilePhoneLine } from "./ProfilePhoneLine";
import { ProfileEmailLine } from "./ProfileEmailLine";
import { ComButtons } from "../../components/drawer/ComButtons";
import { ProfileTimezoneLine } from "./ProfileTimezoneLine.tsx";
import { ProfileLocationLine } from "./ProfileLocationLine";
import { ProfileReferralLinkLine } from "./ProfileReferralLinkLine";

import { NotesContent } from "../notes/NotesContent";
import { useMixPanel } from "../../hooks/useMixPanel";
import { LanguageContext } from "../../hooks/LanguageContext";
import { useTheme } from "@mui/material/styles";

const MemberProfileDrawerContent = ({ isWebView }) => {
  const { userInfo } = useAuth();
  const { logEvent } = useMixPanel();
  const theme = useTheme();

  const {
    memberData,
    customerHrefData,
    apiChainInProgress,
    // customerOrdersLength,  // not used, causes lint error
    customerOrdersInitialLength,
    customerReferrals,
    customerNotes,
    errorMessage,
  } = useContext(MemberContext);
  const { languageDir } = useContext(LanguageContext);
  const location = useLocation();
  const navigate = useNavigate();

  const showSubscriptionChip = false;
  const showCustomerTypeChip = ["Customer", "WholesaleCustomer", "PreferredCustomer"].includes(customerHrefData.type);

  const translatedOrders = useTranslateKey("orders");
  const translatedReferrals = useTranslateKey("referrals");
  const viewPhotoLabel = useTranslateKey("profile_photo");

  const profilePictureUrl = customerHrefData.profilePicture?.sizes[0]?.media;
  const [largeAvatarOpen, setLargeAvatarOpen] = useState(false);
  const isFutureDate = isDateInFuture(memberData.subscriptions?.at(-1)?.endDate)

  // Extract native name from humanName.fullName@{market} field
  const getNativeName = () => {
    if (!customerHrefData?.humanName) return null;
    
    const fullNameField = Object.keys(customerHrefData.humanName).find(key => 
      key.includes('fullName@') && !key.includes('en')
    );
    if (fullNameField) {
      const value = customerHrefData.humanName[fullNameField];
      return value || null;
    }
    return null;
  };
  const nativeName = getNativeName();

  const typographyProps = isFutureDate
    ? {
      "data-test-id": "subscription-status",
      sx: { ...subscriptionDate, display: "flex" },
      style: {
        color: theme.subscription.subscribed.color,
        backgroundColor: theme.subscription.subscribed.backgroundColor,
      }
    }
    : {
      "data-test-id": "subscription-status",
      sx: { ...subscriptionDate },
      style: {
        color: "rgb(127, 145, 168)",
        backgroundColor: "rgb(232, 235, 239)",
      }
    }

  // set boolean based on whether the sponsor for a given member is the current user/distributor
  const showReferrer = () => {
    return customerHrefData.sponsor.id.unicity !== userInfo.unicityId;
  };

  const handleReferrerClick = () => {
    logEvent("open_profile", {
      location: "member_profile_referred_by",
      user_type: "member"
    })
    navigate(`${location.pathname}?drawer=member&id=${customerHrefData.sponsor.id.unicity}&drawerContent=profile`, { state: location });
  };

  const handleArrowClick = () => {
    navigate(-1, { replace: true });
  };

  // if error in any of the three API calls, render MemberErrorScreen with message
  if (errorMessage) {
    return <MemberErrorScreen />;
  }

  // render loading bar while API chain in in progress
  if (apiChainInProgress)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 500,
        }}
      >
        <CircularProgress data-test-id="progress-bar-shown" />
      </Box>
    );

  // Add timezone info to customerHrefData if not present
  if (customerHrefData && !customerHrefData.timezone) {
    dayjs.extend(utc);
    dayjs.extend(timezone);
    customerHrefData.timezone = dayjs.tz.guess(); // Gets user's local timezone
  }

  return (
    <>
      <Box sx={{ padding: "24px" }}>
        <Box sx={{
          display: "flex",
          justifyContent: isWebView ? "flex-end" : "space-between",
          mb: 3
        }}>
          {!isWebView && (languageDir === "ltr" ?
            <KeyboardBackspaceIcon
              onClick={handleArrowClick}
              sx={{ cursor: "pointer" }}
            /> :
            <ArrowForwardIcon
              onClick={handleArrowClick}
              sx={{ cursor: "pointer" }}
            />)}
          <CloseIcon onClick={handleArrowClick} sx={{ cursor: "pointer" }} />
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: "12px",
          }}
        >
          <Box
            sx={{ ...officeAvatarWrapper, cursor: "pointer" }}
            onClick={() => setLargeAvatarOpen(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setLargeAvatarOpen(true);
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={viewPhotoLabel}
            data-test-id="member-profile-avatar-button"
          >
            <Avatar
              id="user-avatar"
              alt={customerHrefData.humanName.firstName}
              src={profilePictureUrl}
              sx={{ width: 40, height: 40 }}
            />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", flexDirection: "column"}}>
              <Typography
                data-test-id="member-profile-name"
                sx={{
                  fontSize: "calc(16px * var(--unicity-font-scale, 1))",
                  fontWeight: "600",
                }}
              >
                {customerHrefData?.humanName?.firstName}{" "}
                {customerHrefData?.humanName?.lastName}
              </Typography>
              {nativeName && (
                <Typography
                  data-test-id="member-profile-native-name"
                  sx={{
                    fontSize: "calc(14px * var(--unicity-font-scale, 1))",
                    fontWeight: "600",
                    opacity: 0.8,
                    marginLeft: "4px",
                  }}
                >
                  {(`${nativeName}`)}
                </Typography>
              )}
            </Box>
            <Typography
              sx={{
                fontSize: 10,
                color: "#7388A1",
              }}
            >
              <span data-test-id="member-profile-uid">
                UID: {customerHrefData.id.unicity}{" "}
              </span>
              <CopyToClipboard
                stringToCopy={`${customerHrefData.id.unicity}`}
                iconId="copyUnicityId"
              />
              {showReferrer() ? (
                <Box component={"span"}>
                  <T data-test-id="member-profile-sponsor">referred_by</T>:
                  <span
                    onClick={handleReferrerClick}
                    style={{ cursor: "pointer" }}
                  >{`${customerHrefData.sponsor.humanName.firstName} ${customerHrefData.sponsor.humanName.lastName}`}</span>
                </Box>
              ) : (
                ""
              )}
            </Typography>
          </Box>
        </Box>
        {
          // show CustomerTypeChip for neccesary customer types
          // https://app.clickup.com/t/863hbdtmt
          showCustomerTypeChip &&
          <Box display="flex" flexDirection="column" alignItems="flex-start" paddingLeft="52px">
            <CustomerTypeChip
              customerType={customerHrefData.type}
              customStyles={{
                fontSize: "10px",
                padding: "4px 8px"
              }}
            />
            {showSubscriptionChip &&
              (memberData.subscriptions) &&
              <Typography
                {...typographyProps}
              >
                {
                  // use the computed boolean
                  isFutureDate
                    ? `Subscription Renewal: ${formatSubscriptionDate(memberData.subscriptions.at(-1).endDate)}`
                    : "No subscription"
                }
              </Typography>
            }
          </Box>
        }
        <ComButtons
          customerHrefData={customerHrefData}
          drawerType="member"
          eventLocation={"member_profile"}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            p: 2,
          }}
        >
          {/* phone section */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ProfilePhoneLine
              customerHrefData={customerHrefData}
              drawerType="member"
              eventLocation="member_profile"
            />
          </Box>

          {/* email section */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ProfileEmailLine
              customerHrefData={customerHrefData}
              drawerType="member"
              eventLocation="member_profile"
            />
          </Box>

          {/* referral link section */}
          <Box sx={{ display: 'flex', gap: 1.7 }}>
            <ProfileReferralLinkLine
              customerHrefData={customerHrefData}
              referralCode={memberData?.referralCode}
              drawerType="member"
              eventLocation="member_profile"
            />
          </Box>

          <ProfileLocationLine
            customerHrefData={customerHrefData}
            // Updated to "member" to keep data-testid consistent (member-profile-location)
            drawerType="member"
          />

          <ProfileTimezoneLine
            customerHrefData={customerHrefData}
            drawerType="member"
          />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "row", gap: "8px" }}>
          <DrawerButton
            id="member-profile-order-button"
            // text={`${translatedOrders} (${customerOrdersLength < 10 ? customerOrdersLength : "10+"})`}
            // isDisabled={customerOrdersLength === 0}
            text={`${translatedOrders} (${customerOrdersInitialLength < 10 ? customerOrdersInitialLength : "10+"})`}
            isDisabled={customerOrdersInitialLength === 0}
            type="orders"
          />
          <DrawerButton
            id="member-profile-referral-button"
            text={`${translatedReferrals} (${customerReferrals?.length})`}
            isDisabled={customerReferrals?.length === 0}
            type="referrals"
          />
        </Box>
      </Box>
      <Divider />
      <NotesContent
        customerNotes={customerNotes}
        customerId={memberData.unicity}
        location="member_profile"
      />
      <LargeAvatarModal
        open={largeAvatarOpen}
        onClose={() => setLargeAvatarOpen(false)}
        name={`${customerHrefData?.humanName?.firstName ?? ""} ${customerHrefData?.humanName?.lastName ?? ""}`}
        imageUrl={profilePictureUrl}
      />
    </>
  );
};

export { MemberProfileDrawerContent };
